import { COMPANY_DATA_TRANSFER_PATHS } from "@/routes/company/routes.data-transfer";
import { MS_CHAT_PATHS } from "@/routes/my-space/routes.chat";
import { COMPANY_TIME_ATTENDANCE_PATHS } from "@/routes/company/routes.time-attendance";
import { COMPANY_ASSETS_PATHS } from "@/routes/company/routes.asset";
import { COMPANY_DIGITAL_ID_PATHS } from "@/routes/company/routes.digital-id";
import { COMPANY_PAYROLLS_PATHS } from "@/routes/company/routes.payroll";
import { COMPANY_DOCUMENTS_PATHS } from "@/routes/company/routes.documents";
import { COMPANY_EMPLOYEES_PATHS } from "@/routes/company/routes.employees";
import { COMPANY_SETTINGS_PATHS } from "@/routes/company/routes.settings";
import { COMPANY_SETUP_PATHS } from "@/routes/company/routes.company-setup";
import { COMPANY_EXPENSES_PATHS } from "@/routes/company/routes.expenses";
import { COMPANY_SALES_PATHS } from "@/routes/company/routes.sales-commissions";
import { COMPANY_BIOMETRIC_DEVICES_PATHS } from "@/routes/company/routes.biometric-devices";
import { COMPANY_NFC_PATHS } from "@/routes/company/routes.nfc";
import { COMPANY_STUDIO_PATHS } from "@/routes/company/routes.studio";
import { COMPANY_MY_APP_PATHS } from "@/routes/company/routes.my-app";
import { MS_MY_APP_PATHS } from "@/routes/my-space/routes.my-app";
import { COMPANY_NOTIFICATIONS_PATHS } from "@/routes/company/routes.notifications";
import { COMPANY_LEAVES_MANAGEMENT_PATHS } from "@/routes/company/routes.leave-management";
import { COMPANY_RECRUITMENT_PATHS } from "@/routes/company/routes.recruitment";
import { COMPANY_INTEGRATIONS_PATHS } from "@/routes/company/routes.integrations";
import { COMPANY_AUDIT_LOGS_PATHS } from "@/routes/company/routes.audit-logs";
import { COMPANY_CAMERA_PATHS } from "@/routes/company/routes.camera";
import { COMPANY_ADD_ONS_PATHS } from "@/routes/company/routes.add-ons";
// Module-settings URLs come from the cycle-free `module-settings.paths.ts`, not
// from the modules' route files. Teams / Performance / Onboarding are referenced
// here ONLY for their settings pages, and their route files sit inside import
// cycles (route file → page → routes/paths.ts → route file) that stay harmless
// only while nothing pulls them in early. Importing them here would be exactly
// such an early pull.
import { MODULE_SETTINGS_PATHS as MS } from "@/routes/company/module-settings.paths";

import { MS_LEAVES_MANAGEMENT_PATHS } from "@/routes/my-space/routes.leave-management";
import { MS_TIME_ATTENDANCE_PATHS } from "@/routes/my-space/routes.time-attendance";
import { MS_ASSETS_PATHS } from "@/routes/my-space/routes.asset";
import { MS_DOCUMENTS_PATHS } from "@/routes/my-space/routes.documents";
import { MS_SALES_PATHS } from "@/routes/my-space/routes.sales-commissions";
import { MS_SETTINGS_PATHS } from "@/routes/my-space/routes.settings";

/**
 * A deep-link inside a module (a tab, sub-section, or "new" form).
 * `path` always references a *static* PATHS member (never a dynamic `:id`
 * route) so it is guaranteed navigable without an entity.
 */
export interface ISubPage {
  label: string;
  path: string;
  keywords?: string[];
  /** Section label between module and page — renders as a third breadcrumb level. */
  parentLabel?: string;
}

/**
 * Sub-pages keyed by their parent module's ROOT path. A group is only surfaced
 * in search when the user can access its parent module — so this never leaks a
 * page the user's role can't open. To add depth to a module, add an entry here;
 * only reference static (string) PATHS members so the link can never 404.
 */
export interface ISubPageGroup {
  /** Parent module ROOT path. Must equal the sidebar link's `/${key}` url. */
  parentRoot: string;
  items: ISubPage[];
}

/**
 * Breadcrumb level shown between a module and one of its settings sub-sections
 * (`Workspace › Time & Attendance › Settings › Shift Settings`). Module settings
 * pages all use the same label, so keep it in one place.
 */
const SETTINGS_LABEL = "Settings";

export const SUBPAGE_GROUPS: ISubPageGroup[] = [
  // ── Company ────────────────────────────────────────────────────────────────
  {
    parentRoot: COMPANY_DATA_TRANSFER_PATHS.ROOT,
    items: [
      {
        label: "Import Data",
        path: COMPANY_DATA_TRANSFER_PATHS.IMPORT,
        keywords: ["import", "upload", "migrate", "migration", "bulk", "spreadsheet", "excel", "xlsx", "template"],
      },
      {
        label: "Download Data",
        path: COMPANY_DATA_TRANSFER_PATHS.EXPORT,
        keywords: ["export", "download", "backup", "excel", "spreadsheet", "extract", "bulk edit"],
      },
      {
        label: "Transfer History",
        path: COMPANY_DATA_TRANSFER_PATHS.HISTORY,
        keywords: ["history", "runs", "past imports", "undo", "rollback"],
      },
    ],
  },
  {
    parentRoot: COMPANY_TIME_ATTENDANCE_PATHS.ROOT,
    items: [
      { label: "Overview", path: COMPANY_TIME_ATTENDANCE_PATHS.OVERVIEW },
      {
        label: "Regularization Requests",
        path: COMPANY_TIME_ATTENDANCE_PATHS.REGULARIZATION,
        keywords: ["regularize", "correction"],
      },
      {
        label: "Create Regularization",
        path: COMPANY_TIME_ATTENDANCE_PATHS.ADD_REGULARIZATION,
        keywords: ["new", "add"],
      },
      { label: "Overtime Requests", path: COMPANY_TIME_ATTENDANCE_PATHS.OVERTIME },
      {
        label: "Create Overtime",
        path: COMPANY_TIME_ATTENDANCE_PATHS.ADD_OVERTIME,
        keywords: ["new", "add", "ot"],
      },
      {
        label: "Attendance Exceptions",
        path: COMPANY_TIME_ATTENDANCE_PATHS.ATTENDANCE_EXCEPTIONS,
      },
      // ── Module settings (moved out of Company Settings) ───────────────────
      {
        label: "Time & Attendance Settings",
        path: MS.TIME_ATTENDANCE.ROOT,
        keywords: ["settings", "attendance rules", "clock in", "configure"],
      },
      { label: "Quick Activity (Breaks)",     path: MS.TIME_ATTENDANCE.VTAB_QUICK_ACTIVITY, parentLabel: SETTINGS_LABEL, keywords: ["breaks", "rest", "lunch"] },
      { label: "Attendance Reminders",        path: MS.TIME_ATTENDANCE.VTAB_REMINDERS,      parentLabel: SETTINGS_LABEL, keywords: ["notifications", "clock in reminder"] },
      { label: "Access & Verification Rules", path: MS.TIME_ATTENDANCE.VTAB_RULES,          parentLabel: SETTINGS_LABEL, keywords: ["biometric", "gps", "geofence", "face"] },
      { label: "Default Attendance Rule",     path: MS.TIME_ATTENDANCE.VTAB_DEFAULT_RULE,   parentLabel: SETTINGS_LABEL, keywords: ["default rule", "attendance policy"] },
      { label: "Shift Settings",              path: MS.TIME_ATTENDANCE.VTAB_SHIFTS,         parentLabel: SETTINGS_LABEL, keywords: ["shifts", "schedule", "roster", "work hours"] },
      { label: "Overtime Policy",             path: MS.TIME_ATTENDANCE.VTAB_OVERTIME,       parentLabel: SETTINGS_LABEL, keywords: ["ot", "overtime config", "ot rules"] },
    ],
  },
  {
    parentRoot: COMPANY_LEAVES_MANAGEMENT_PATHS.ROOT,
    items: [
      { label: "All Leaves", path: COMPANY_LEAVES_MANAGEMENT_PATHS.ALL_LEAVES },
      {
        label: "Apply for Leave",
        path: COMPANY_LEAVES_MANAGEMENT_PATHS.APPLY,
        keywords: ["request", "vacation", "time off", "pto"],
      },
      {
        label: "Workflow Requests",
        path: COMPANY_LEAVES_MANAGEMENT_PATHS.WORKFLOW_REQUESTS,
        keywords: ["approval"],
      },
      // ── Module settings (moved out of Company Settings) ───────────────────
      {
        label: "Leave Settings",
        path: MS.LEAVE_MANAGEMENT.ROOT,
        keywords: ["settings", "pto", "vacation", "leave policy", "time off"],
      },
      { label: "Leave Group",      path: MS.LEAVE_MANAGEMENT.VTAB_GROUP,    parentLabel: SETTINGS_LABEL, keywords: ["group", "bundle", "leave types group"] },
      { label: "Leave Type",       path: MS.LEAVE_MANAGEMENT.VTAB_TYPE,     parentLabel: SETTINGS_LABEL, keywords: ["annual", "sick", "pto", "vacation types", "entitlement", "days allocation", "leave balance"] },
      { label: "Leave Policy",     path: MS.LEAVE_MANAGEMENT.VTAB_POLICY,   parentLabel: SETTINGS_LABEL, keywords: ["rollover", "unused leave", "carry forward", "leave encash", "leave payment", "encashment"] },
      { label: "Blackout Periods", path: MS.LEAVE_MANAGEMENT.VTAB_BLACKOUT, parentLabel: SETTINGS_LABEL, keywords: ["blocked dates", "no leave period"] },
    ],
  },
  {
    parentRoot: COMPANY_ASSETS_PATHS.ROOT,
    items: [
      { label: "Regular Assets", path: COMPANY_ASSETS_PATHS.REGULAR_ASSETS },
      { label: "Consumable Assets", path: COMPANY_ASSETS_PATHS.CONSUMABLE_ASSETS },
      {
        label: "Asset Requests",
        path: COMPANY_ASSETS_PATHS.ASSET_REQUESTS,
        keywords: ["approval"],
      },
      {
        label: "Create Asset",
        path: COMPANY_ASSETS_PATHS.CREATE,
        keywords: ["new", "add"],
      },
      // ── Module settings (moved out of Company Settings) ───────────────────
      {
        label: "Inventory & Assets Settings",
        path: MS.ASSETS.ROOT,
        keywords: ["settings", "assets config", "inventory config"],
      },
      { label: "Asset Types",  path: MS.ASSETS.VTAB_ASSET_TYPES,  parentLabel: SETTINGS_LABEL, keywords: ["asset categories", "equipment types"] },
      { label: "Asset Brands", path: MS.ASSETS.VTAB_ASSET_BRANDS, parentLabel: SETTINGS_LABEL, keywords: ["manufacturers", "vendors"] },
    ],
  },
  {
    parentRoot: COMPANY_DIGITAL_ID_PATHS.ROOT,
    items: [
      {
        label: "Employees",
        path: COMPANY_DIGITAL_ID_PATHS.EMPLOYEES,
        keywords: ["digital id", "business card", "id card", "rollout"],
      },
      // ── Module settings (moved out of Company Settings) ───────────────────
      {
        label: "Digital ID Settings",
        path: MS.DIGITAL_ID.ROOT,
        keywords: ["settings", "digital id card", "brand lock"],
      },
      {
        label: "Brand Lock",
        path: MS.DIGITAL_ID.VTAB_BRAND_LOCK,
        parentLabel: SETTINGS_LABEL,
        keywords: ["logo", "brand color", "templates", "card templates"],
      },
    ],
  },
  {
    parentRoot: COMPANY_MY_APP_PATHS.ROOT,
    items: [
      {
        label: "App Store",
        path: COMPANY_MY_APP_PATHS.APP_STORE,
        keywords: ["app store", "marketplace", "catalog", "install", "browse apps"],
      },
    ],
  },
  {
    parentRoot: MS_MY_APP_PATHS.ROOT,
    items: [
      {
        label: "App Store",
        path: MS_MY_APP_PATHS.APP_STORE,
        keywords: ["app store", "marketplace", "catalog", "install", "browse apps"],
      },
    ],
  },
  {
    parentRoot: COMPANY_PAYROLLS_PATHS.ROOT,
    items: [
      { label: "Pay Slips", path: COMPANY_PAYROLLS_PATHS.PAY_SLIPS, keywords: ["payslip", "salary"] },
      { label: "Payment Files", path: COMPANY_PAYROLLS_PATHS.PAYMENT_FILES },
      {
        label: "Final Settlements",
        path: COMPANY_PAYROLLS_PATHS.FINAL_SETTLEMENTS,
        keywords: ["eosb", "gratuity"],
      },
      {
        label: "Generate Payroll",
        path: COMPANY_PAYROLLS_PATHS.GENERATE,
        keywords: ["run", "process"],
      },
      // ── Module settings (moved out of Company Settings — includes the former
      // Compensation Settings tab) ─────────────────────────────────────────
      {
        label: "Payroll Settings",
        path: MS.PAYROLL.ROOT,
        keywords: ["settings", "payroll config", "wps", "compensation"],
      },
      { label: "Setup Overview",           path: MS.PAYROLL.VTAB_OVERVIEW,      parentLabel: SETTINGS_LABEL, keywords: ["payroll setup", "checklist", "getting started", "onboarding"] },
      { label: "Payroll Policies",         path: MS.PAYROLL.VTAB_POLICIES,      parentLabel: SETTINGS_LABEL, keywords: ["payroll rules", "wps"] },
      { label: "Country Rules",            path: MS.PAYROLL.VTAB_COUNTRY_RULES, parentLabel: SETTINGS_LABEL, keywords: ["local regulations", "tax"] },
      { label: "Branch Configuration",     path: MS.PAYROLL.VTAB_BRANCH,        parentLabel: SETTINGS_LABEL, keywords: ["branch pay", "payroll by branch", "branch payroll configure"] },
      { label: "Earning Types",            path: MS.PAYROLL.VTAB_PAY_TYPES,     parentLabel: SETTINGS_LABEL, keywords: ["pay types", "payment types", "compensation"] },
      { label: "Earnings",                 path: MS.PAYROLL.VTAB_PAY,           parentLabel: SETTINGS_LABEL, keywords: ["pay", "pay components", "salary components", "compensation"] },
      { label: "Deductions",               path: MS.PAYROLL.VTAB_DEDUCTIONS,    parentLabel: SETTINGS_LABEL, keywords: ["cuts", "withholding", "compensation"] },
      { label: "Benefits",                 path: MS.PAYROLL.VTAB_BENEFITS,      parentLabel: SETTINGS_LABEL, keywords: ["perks", "allowances", "insurance", "compensation"] },
    ],
  },
  {
    parentRoot: COMPANY_DOCUMENTS_PATHS.ROOT,
    items: [
      { label: "Employee Documents", path: COMPANY_DOCUMENTS_PATHS.EMPLOYEE_DOCUMENTS },
      { label: "Company Documents", path: COMPANY_DOCUMENTS_PATHS.COMPANY_DOCUMENTS },
      { label: "Sent Requests", path: COMPANY_DOCUMENTS_PATHS.SENT_REQUESTS },
      { label: "Received Requests", path: COMPANY_DOCUMENTS_PATHS.RECEIVED_REQUESTS },
      // ── Module settings (moved out of Company Settings) ───────────────────
      {
        label: "Documents Settings",
        path: MS.DOCUMENTS.ROOT,
        keywords: ["settings", "document config"],
      },
      { label: "Document Types", path: MS.DOCUMENTS.VTAB_DOC_TYPES, parentLabel: SETTINGS_LABEL, keywords: ["file types", "document categories"] },
    ],
  },
  {
    parentRoot: COMPANY_EMPLOYEES_PATHS.ROOT,
    items: [
      { label: "Draft", path: COMPANY_EMPLOYEES_PATHS.DRAFT },
      { label: "Instant Onboarded", path: COMPANY_EMPLOYEES_PATHS.INSTANT },
      { label: "Active Employees", path: COMPANY_EMPLOYEES_PATHS.ACTIVE },
      { label: "All Employees", path: COMPANY_EMPLOYEES_PATHS.ALL },
      { label: "Offboarded", path: COMPANY_EMPLOYEES_PATHS.OFFBOARDED },
      {
        label: "Add Employee",
        path: COMPANY_EMPLOYEES_PATHS.ADD,
        keywords: ["new", "hire", "create"],
      },
      { label: "Onboarding", path: COMPANY_EMPLOYEES_PATHS.ONBOARDING },
      { label: "Instant Onboarding", path: COMPANY_EMPLOYEES_PATHS.INSTANT_ONBOARDING },
    ],
  },
  {
    parentRoot: COMPANY_EXPENSES_PATHS.ROOT,
    items: [
      { label: "All Expenses", path: COMPANY_EXPENSES_PATHS.ALL },
      { label: "Reimbursements", path: COMPANY_EXPENSES_PATHS.REIMBURSEMENTS },
      { label: "Requests", path: COMPANY_EXPENSES_PATHS.REQUESTS, keywords: ["approval"] },
      { label: "Create Expense", path: COMPANY_EXPENSES_PATHS.CREATE, keywords: ["new", "add"] },
      // ── Module settings (moved out of Company Settings) ───────────────────
      {
        label: "Expenses Settings",
        path: MS.EXPENSES.ROOT,
        keywords: ["settings", "expense policy", "reimbursement"],
      },
      { label: "Expense Categories", path: MS.EXPENSES.VTAB_CATEGORIES, parentLabel: SETTINGS_LABEL, keywords: ["expense types", "cost categories"] },
    ],
  },
  {
    parentRoot: COMPANY_SALES_PATHS.ROOT,
    items: [
      { label: "All Sales", path: COMPANY_SALES_PATHS.ALL, keywords: ["revenue", "deals"] },
      { label: "Sale Approvals", path: COMPANY_SALES_PATHS.REQUESTS, keywords: ["approval", "review", "pending sales"] },
      { label: "Commissions", path: COMPANY_SALES_PATHS.COMMISSIONS, keywords: ["commission", "earnings", "payout"] },
      { label: "Log Sale", path: COMPANY_SALES_PATHS.CREATE, keywords: ["new", "add", "create", "on behalf"] },
      // ── Module settings ───────────────────────────────────────────────────
      {
        label: "Sales & Commissions Settings",
        path: MS.SALES_COMMISSIONS.ROOT,
        keywords: ["settings", "commission plan", "commission rate"],
      },
      {
        label: "Commission Plans",
        path: MS.SALES_COMMISSIONS.VTAB_PLANS,
        parentLabel: SETTINGS_LABEL,
        keywords: ["commission plan", "tiers", "percentage", "rate", "brackets"],
      },
    ],
  },
  {
    parentRoot: COMPANY_BIOMETRIC_DEVICES_PATHS.ROOT,
    items: [
      { label: "Devices", path: COMPANY_BIOMETRIC_DEVICES_PATHS.DEVICES, keywords: ["biometric", "fingerprint", "terminal", "hikvision", "camera", "add device"] },
      { label: "Unlinked IDs", path: COMPANY_BIOMETRIC_DEVICES_PATHS.UNMAPPED_EVENTS, keywords: ["unmapped", "unlinked", "unresolved", "punch", "link"] },
      // ── Module settings ───────────────────────────────────────────────────
      {
        label: "Biometric Devices Settings",
        path: MS.BIOMETRIC_DEVICES.ROOT,
        keywords: ["settings", "agent", "pairing code"],
      },
      {
        label: "MaiHRMS Desktop",
        path: MS.BIOMETRIC_DEVICES.VTAB_AGENTS,
        parentLabel: SETTINGS_LABEL,
        keywords: ["desktop", "pc", "agent", "pairing code", "on-prem"],
      },
      {
        label: "Direct Devices",
        path: MS.BIOMETRIC_DEVICES.VTAB_DIRECT_TERMINALS,
        parentLabel: SETTINGS_LABEL,
        keywords: ["direct", "cloud", "websocket", "maihrms", "terminal", "camera"],
      },
    ],
  },
  {
    parentRoot: COMPANY_NFC_PATHS.ROOT,
    items: [
      { label: "Assigned Chips", path: COMPANY_NFC_PATHS.CHIPS },
      { label: "Analytics", path: COMPANY_NFC_PATHS.ANALYTICS },
    ],
  },
  {
    parentRoot: COMPANY_STUDIO_PATHS.ROOT,
    items: [
      {
        label: "Workflows",
        path: COMPANY_STUDIO_PATHS.WORKFLOWS_ALL,
        keywords: ["workflow", "automation", "approval", "flow", "builder"],
      },
      {
        label: "Forms",
        path: COMPANY_STUDIO_PATHS.FORMS_ALL,
        keywords: ["form", "survey", "nfc form", "builder"],
      },
      {
        label: "Documents",
        path: COMPANY_STUDIO_PATHS.DOCUMENTS_ALL,
        keywords: ["document", "template", "doc", "letter", "builder"],
      },
      {
        label: "Email Templates",
        path: COMPANY_STUDIO_PATHS.EMAIL_TEMPLATES_ALL,
        keywords: ["email", "template", "mail", "message", "builder"],
      },
    ],
  },
  {
    parentRoot: COMPANY_NOTIFICATIONS_PATHS.ROOT,
    items: [
      { label: "All",    path: COMPANY_NOTIFICATIONS_PATHS.TAB_ALL },
      { label: "Unread", path: COMPANY_NOTIFICATIONS_PATHS.TAB_UNREAD, keywords: ["unseen", "new"] },
    ],
  },
  {
    parentRoot: COMPANY_RECRUITMENT_PATHS.ROOT,
    items: [
      { label: "Dashboard", path: COMPANY_RECRUITMENT_PATHS.TAB_DASHBOARD },
      { label: "Job Openings", path: COMPANY_RECRUITMENT_PATHS.TAB_JOBS, keywords: ["jobs", "postings", "openings"] },
      { label: "Candidates", path: COMPANY_RECRUITMENT_PATHS.TAB_CANDIDATES, keywords: ["applicants"] },
      { label: "Interviews", path: COMPANY_RECRUITMENT_PATHS.TAB_INTERVIEWS, keywords: ["schedule", "scorecard"] },
      {
        label: "Assessments",
        path: COMPANY_RECRUITMENT_PATHS.TAB_ASSESSMENTS,
        keywords: ["assessment", "library", "skills test", "coding challenge", "aptitude", "quiz", "questions"],
      },
      {
        label: "New Assessment",
        path: COMPANY_RECRUITMENT_PATHS.ASSESSMENT_FORM_NEW,
        keywords: ["create assessment", "build test", "generate assessment", "ai assessment"],
      },
      { label: "Offers", path: COMPANY_RECRUITMENT_PATHS.TAB_OFFERS },
      {
        label: "Talent Pool",
        path: COMPANY_RECRUITMENT_PATHS.TAB_TALENT_POOL,
        // AI sourcing is no longer its own tab — "Find matching candidates" on
        // Talent Pool replaced it, so the old search terms have to land here.
        keywords: ["pool", "referrals", "sourcing", "ai sourcing", "past applicants", "match"],
      },
      {
        label: "New Job",
        path: COMPANY_RECRUITMENT_PATHS.JOB_FORM_NEW,
        keywords: ["create job", "post a role", "new opening", "job description"],
      },
    ],
  },
  {
    // Teams module — its settings page moved here out of Company Settings.
    parentRoot: MS.TEAMS.MODULE_ROOT,
    items: [
      {
        label: "Team Settings",
        path: MS.TEAMS.ROOT,
        keywords: ["settings", "configure", "team config"],
      },
      {
        label: "Roles & Responsibilities",
        path: MS.TEAMS.VTAB_ROLES_RESPS,
        parentLabel: SETTINGS_LABEL,
        keywords: ["job responsibilities", "role description"],
      },
    ],
  },
  {
    // Performance module — its settings page moved here out of Company Settings.
    parentRoot: MS.PERFORMANCE.MODULE_ROOT,
    items: [
      {
        label: "Performance Settings",
        path: MS.PERFORMANCE.ROOT,
        keywords: ["settings", "kpi", "goals", "appraisal", "review"],
      },
      { label: "Performance Metrics", path: MS.PERFORMANCE.VTAB_METRICS, parentLabel: SETTINGS_LABEL, keywords: ["kpi", "goals", "targets"] },
      { label: "Add Performance Metric", path: MS.PERFORMANCE.METRICS_CREATE, parentLabel: SETTINGS_LABEL, keywords: ["new", "add", "kpi"] },
    ],
  },
  {
    // Onboarding module — its settings page moved here out of Company Settings.
    parentRoot: MS.ONBOARDING.MODULE_ROOT,
    items: [
      {
        label: "Onboarding Settings",
        path: MS.ONBOARDING.ROOT,
        keywords: ["settings", "new hire", "welcome"],
      },
      { label: "Digital Systems", path: MS.ONBOARDING.VTAB_DIGITAL_SYSTEMS, parentLabel: SETTINGS_LABEL, keywords: ["tools", "software", "apps", "systems"] },
    ],
  },
  {
    // Recruitment module — settings for AI screening/sourcing/interview automation.
    parentRoot: MS.RECRUITMENT.MODULE_ROOT,
    items: [
      {
        label: "Recruitment Settings",
        path: MS.RECRUITMENT.ROOT,
        keywords: ["settings", "hiring", "ai", "automation"],
      },
      { label: "Screening", path: MS.RECRUITMENT.VTAB_SCREENING, parentLabel: SETTINGS_LABEL, keywords: ["auto-screen", "auto-decline", "auto-shortlist"] },
      { label: "Match Weighting", path: MS.RECRUITMENT.VTAB_MATCH_WEIGHTING, parentLabel: SETTINGS_LABEL, keywords: ["skills", "experience", "education", "culture"] },
      { label: "Interview Settings", path: MS.RECRUITMENT.VTAB_INTERVIEWS, parentLabel: SETTINGS_LABEL, keywords: ["ai interviews", "integrity", "proctoring"] },
      { label: "Email Templates", path: MS.RECRUITMENT.VTAB_EMAIL_TEMPLATES, parentLabel: SETTINGS_LABEL, keywords: ["invite", "reminder", "offer", "rejection"] },
    ],
  },
  {
    parentRoot: COMPANY_SETTINGS_PATHS.ROOT,
    items: [
      // The company setup ("go-live") guide. It lives under Settings in search
      // because it is gated on the same `settings` section — not under the
      // employee Onboarding module, whose name it deliberately avoids.
      { label: "Setup Guide",             path: COMPANY_SETUP_PATHS.ROOT,                   keywords: ["onboarding", "go live", "golive", "getting started", "checklist", "setup", "wizard"] },

      // ── hTab sections ───────────────────────────────────────────────────────
      { label: "Organization Settings",   path: COMPANY_SETTINGS_PATHS.TAB_ORGANIZATION,    keywords: ["org", "brand", "logo"] },
      { label: "Roles & Permissions",     path: COMPANY_SETTINGS_PATHS.TAB_ROLES,           keywords: ["rbac", "access", "permissions", "user roles"] },
      { label: "Company Policies",        path: COMPANY_SETTINGS_PATHS.TAB_COMPANY_POLICIES,keywords: ["policy", "rules", "handbook"] },
      { label: "Templates",               path: COMPANY_SETTINGS_PATHS.TAB_TEMPLATES,       keywords: ["document template", "contract", "letter"] },
      { label: "Calendars",               path: COMPANY_SETTINGS_PATHS.TAB_CALENDARS,       keywords: ["holiday", "working days", "public holiday"] },

      // ── Organization Settings vTabs ─────────────────────────────────────────
      { label: "Company Information",       path: COMPANY_SETTINGS_PATHS.VTAB_ORG_COMPANY_INFO, parentLabel: "Organization Settings", keywords: ["org info", "company details", "logo", "brand"] },
      { label: "Branches",                  path: COMPANY_SETTINGS_PATHS.VTAB_ORG_BRANCHES,     parentLabel: "Organization Settings", keywords: ["offices", "locations", "branch"] },
      { label: "Departments",               path: COMPANY_SETTINGS_PATHS.VTAB_ORG_DEPARTMENTS,  parentLabel: "Organization Settings", keywords: ["divisions", "teams", "units"] },
      { label: "Positions or Designations", path: COMPANY_SETTINGS_PATHS.VTAB_ORG_POSITIONS,    parentLabel: "Organization Settings", keywords: ["job titles", "roles", "designations"] },
      { label: "Organizational Structure",  path: COMPANY_SETTINGS_PATHS.VTAB_ORG_STRUCTURE,    parentLabel: "Organization Settings", keywords: ["hierarchy", "org chart", "tree"] },

      // ── Roles & Permissions vTabs (the first is the hTab entry above) ───────
      { label: "User Roles", path: COMPANY_SETTINGS_PATHS.VTAB_ROLES_USER_ROLES, parentLabel: "Roles & Permissions", keywords: ["who has role", "assigned roles", "employee roles"] },


      // ── Company Policies vTabs ──────────────────────────────────────────────
      { label: "Policy Categories", path: COMPANY_SETTINGS_PATHS.VTAB_POLICIES_CATEGORIES, parentLabel: "Company Policies", keywords: ["policy groups"] },
      { label: "Policies",          path: COMPANY_SETTINGS_PATHS.VTAB_POLICIES_LIST,        parentLabel: "Company Policies", keywords: ["rules", "handbook", "company rules"] },


      // ── Calendars vTabs ─────────────────────────────────────────────────────
      { label: "Holiday Types",    path: COMPANY_SETTINGS_PATHS.VTAB_CAL_TYPES,    parentLabel: "Calendars", keywords: ["public holiday types", "calendar categories"] },
      { label: "Holiday Calendar", path: COMPANY_SETTINGS_PATHS.VTAB_CAL_HOLIDAYS, parentLabel: "Calendars", keywords: ["public holidays", "national holidays", "working days"] },
    ],
  },

  // ── My Space ─────────────────────────────────────────────────────────────────
  {
    parentRoot: MS_LEAVES_MANAGEMENT_PATHS.ROOT,
    items: [
      {
        label: "Apply for Leave",
        path: MS_LEAVES_MANAGEMENT_PATHS.APPLY,
        keywords: ["request", "vacation", "time off", "pto"],
      },
    ],
  },
  {
    parentRoot: MS_TIME_ATTENDANCE_PATHS.ROOT,
    items: [
      { label: "Attendance Summary", path: MS_TIME_ATTENDANCE_PATHS.ATTENDANCE_SUMMARY },
      {
        label: "Regularization Requests",
        path: MS_TIME_ATTENDANCE_PATHS.REGULARIZATION,
        keywords: ["regularize", "correction"],
      },
      {
        label: "Create Regularization",
        path: MS_TIME_ATTENDANCE_PATHS.ADD_REGULARIZATION,
        keywords: ["new", "add"],
      },
      { label: "Overtime Requests", path: MS_TIME_ATTENDANCE_PATHS.OVERTIME },
      {
        label: "Request Overtime",
        path: MS_TIME_ATTENDANCE_PATHS.ADD_OVERTIME,
        keywords: ["new", "add", "ot"],
      },
      {
        label: "Attendance Exceptions",
        path: MS_TIME_ATTENDANCE_PATHS.ATTENDANCE_EXCEPTIONS,
      },
    ],
  },
  {
    parentRoot: MS_ASSETS_PATHS.ROOT,
    items: [
      { label: "Assigned Assets", path: MS_ASSETS_PATHS.ASSIGNED_ASSETS },
      { label: "Requested Assets", path: MS_ASSETS_PATHS.REQUESTED_ASSETS },
      {
        label: "New Asset Request",
        path: MS_ASSETS_PATHS.REQUEST,
        keywords: ["new", "add"],
      },
    ],
  },
  {
    parentRoot: MS_SALES_PATHS.ROOT,
    items: [
      { label: "My Sales", path: MS_SALES_PATHS.MY_SALES, keywords: ["revenue", "deals"] },
      {
        label: "My Commission",
        path: MS_SALES_PATHS.MY_COMMISSION,
        keywords: ["commission", "earnings", "my plan", "commission plan"],
      },
      {
        label: "Log a Sale",
        path: MS_SALES_PATHS.CREATE,
        keywords: ["new", "add", "record sale", "submit sale"],
      },
    ],
  },
  {
    parentRoot: MS_DOCUMENTS_PATHS.ROOT,
    items: [
      { label: "My Documents", path: MS_DOCUMENTS_PATHS.MY_DOCUMENTS },
      { label: "Sent Requests", path: MS_DOCUMENTS_PATHS.SENT_REQUESTS },
      { label: "Received Requests", path: MS_DOCUMENTS_PATHS.RECEIVED_REQUESTS },
      { label: "Signed Documents", path: MS_DOCUMENTS_PATHS.SIGNED_DOCUMENTS },
    ],
  },
  {
    parentRoot: MS_SETTINGS_PATHS.ROOT,
    items: [
      { label: "Profile Settings",       path: MS_SETTINGS_PATHS.TAB_PROFILE,       keywords: ["personal", "avatar", "name", "contact"] },
      { label: "Security Settings",      path: MS_SETTINGS_PATHS.TAB_SECURITY,      keywords: ["password", "2fa", "two factor", "mfa"] },
      { label: "Notification Settings",  path: MS_SETTINGS_PATHS.TAB_NOTIFICATIONS, keywords: ["alerts", "email", "push", "reminders"] },
    ],
  },
  {
    parentRoot: COMPANY_AUDIT_LOGS_PATHS.ROOT,
    items: [
      {
        label: "Audit Log Settings",
        path: COMPANY_AUDIT_LOGS_PATHS.SETTINGS,
        parentLabel: SETTINGS_LABEL,
        keywords: ["retention", "purge", "keep logs"],
      },
    ],
  },
  {
    parentRoot: COMPANY_CAMERA_PATHS.ROOT,
    items: [
      {
        label: "Camera Settings",
        path: COMPANY_CAMERA_PATHS.SETTINGS,
        parentLabel: SETTINGS_LABEL,
        keywords: ["retention", "recording", "footage", "storage"],
      },
    ],
  },
];

/**
 * Extra search keywords for top-level modules, keyed by module ROOT path.
 * Helps users find a module by synonym (e.g. "vacation" → Leave Management).
 * Optional — a module with no entry is still searchable by its display name.
 */
export const MODULE_KEYWORDS: Record<string, string[]> = {
  [COMPANY_LEAVES_MANAGEMENT_PATHS.ROOT]: ["vacation", "time off", "pto", "holiday"],
  [MS_LEAVES_MANAGEMENT_PATHS.ROOT]: ["vacation", "time off", "pto", "holiday"],
  [COMPANY_TIME_ATTENDANCE_PATHS.ROOT]: ["clock", "shift", "overtime", "ot"],
  [MS_TIME_ATTENDANCE_PATHS.ROOT]: ["clock", "shift", "overtime", "ot"],
  [COMPANY_PAYROLLS_PATHS.ROOT]: ["salary", "payslip", "wages"],
  [COMPANY_EMPLOYEES_PATHS.ROOT]: ["staff", "people", "team members"],
  [COMPANY_SALES_PATHS.ROOT]: ["commission", "revenue", "deals", "invoices"],
  [COMPANY_AUDIT_LOGS_PATHS.ROOT]: ["logs", "history", "activity", "compliance", "trail", "who did what"],
  [MS_SALES_PATHS.ROOT]: ["commission", "revenue", "deals", "invoices"],
  [COMPANY_INTEGRATIONS_PATHS.ROOT]: ["jira", "atlassian", "connect", "oauth"],
  [COMPANY_BIOMETRIC_DEVICES_PATHS.ROOT]: ["biometric", "fingerprint", "hikvision", "terminal", "clock in device"],
  [COMPANY_CAMERA_PATHS.ROOT]: ["cctv", "recording", "playback", "video", "footage", "face camera"],
  [COMPANY_ADD_ONS_PATHS.ROOT]: ["addons", "add-on", "plan", "upgrade", "storage", "cameras", "recording plan", "subscribe"],
  [MS_CHAT_PATHS.ROOT]: ["messages", "conversation", "inbox", "group", "broadcast", "dm"],
};
